import { useState, useEffect, useRef, createContext } from "react";
import axios from "axios";
import "@fortawesome/fontawesome-free/css/all.min.css";
import BASE_URL from "../../assets/global/url";
import { Link, useNavigate } from "react-router-dom";
import { ThreeDot } from "react-loading-indicators";
import NoAccess from "../../assets/img/NoAccess.png";
import swal from "sweetalert";
import useDecodeToken from "../../hooks/customHook/useDecodeToken";
import { format } from "date-fns";
import DateRangePicker from "../../components/DateRangePicker";
import { PaginationControls } from "../../hooks/customHook/paginationHook/usePagination";
import { useServerPagination } from "../../hooks/customHook/paginationHook/useServerPagination";
import { useValidPage } from "../../hooks/customHook/paginationHook/useValidPage";
import {
  formatCurrency,
  formatToTwoDecimal,
  parseNumber,
} from "../../utils/numberFormatter";
import CurrencySelector from "../../components/CurrencySelector";
import { truncateText } from "../../utils/textFormatter";
import { Modal, Button, Tabs, Tab } from "react-bootstrap";
import { TransitionGroup, CSSTransition } from "react-transition-group";
import PendingStatusTab from "./component/Payable/PendingStatusTab";
import ApprovedStatusTab from "./component/Payable/ApprovedStatusTab";
import ExportOptionsModal from "../../components/Export/ExportOptionsModal";
import PdfPreviewModal, {
  usePdfPreview,
} from "../../components/Export/PdfPreviewModal";
import payableExportConfig from "./component/Payable/Export/payableExportConfig";
import { getMonthBoundaries, initializeCutoff } from "../../utils/newdate";
import PayableReOpenModal from "./component/Payable/PayableReOpenModal";
import FilterStore from "../../hooks/customHook/FilterStore";
import WidgetInfoIcon from "./component/WidgetInfoIcon";
import { NoTranslate } from "../../helper/prevent_translate_browser";

const ActionMenu = ({
  row,
  handleDeletePayable,
  getItemsToReturn,
  setShowReturnItemsModal,
}) => {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const menuRef = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggle = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + 4,
        left: rect.right - 140,
      });
    }
    setOpen((prev) => !prev);
  };

  return (
    <div
      ref={menuRef}
      style={{ position: "relative", display: "inline-block" }}
    >
      {/* Inline styles for the dot rotation animation */}
      <style>{`
        .payable-kebab-btn .dots-icon {
          display: inline-block;
          transition: transform 0.3s ease;
        }
        .payable-kebab-btn.is-open .dots-icon {
          transform: rotate(90deg);
        }
        .payable-kebab-dropdown button:hover {
          background-color: #f5f5f5;
        }
      `}</style>

      {/* 3-dot trigger button — rounded square style matching the design */}
      <button
        ref={btnRef}
        className={`payable-kebab-btn ${open ? "is-open" : ""}`}
        onClick={handleToggle}
        title="Actions"
        style={{
          borderRadius: "8px",
          width: "36px",
          height: "36px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
        }}
      >
        <i
          className="fas fa-ellipsis dots-icon"
          style={{ fontSize: "1rem" }}
        ></i>
      </button>

      {/* Dropdown menu */}
      {open && (
        <div
          className="payable-kebab-dropdown"
          style={{
            position: "fixed",
            top: menuPos.top,
            left: menuPos.left,
            zIndex: 9999,
            borderRadius: "6px",
            minWidth: "140px",
          }}
        >
          {/* Delete option — always visible, text only in red */}
          <button
            className="payable-kebab-delete bg-transparent border-0 w-100 text-start px-3 py-2"
            style={{
              cursor: "pointer",
              fontSize: "0.9rem",
              fontWeight: "500",
            }}
            onClick={() => {
              setOpen(false);
              handleDeletePayable(row.id, row.purchaseDate, row.transaction_id);
            }}
          >
            <NoTranslate>Delete</NoTranslate>
          </button>

          {/* Return Items option — only when Approved, text only in orange */}
          {row.status === "Approved" && !row.isAdded && (
            <button
              className="payable-kebab-return bg-transparent border-0 w-100 text-start px-3 py-2"
              style={{
                cursor: "pointer",
                fontSize: "0.9rem",
                fontWeight: "500",
              }}
              onClick={() => {
                setOpen(false);
                getItemsToReturn(row.id, row.transaction_id);
                setShowReturnItemsModal(true);
              }}
            >
              <NoTranslate>Return Items</NoTranslate>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const Payable = ({ authrztn }) => {
  const userLoggedID = useDecodeToken();
  const backloadPagination = useServerPagination(
    `${BASE_URL}/payable/backload/products-to-return`,
    10,
  );

  const validtabs = ["pending", "approved"];
  const [activeTab, setActiveTab] = useState(() => {
    const saved = FilterStore.getTab();
    return validtabs.includes(saved) ? saved : "pending";
  });

  const handleTabSelect = (key) => {
    setActiveTab(key);
    FilterStore.saveTab(key);
  };

  const [refreshKey, setRefreshKey] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);

  const fetchAllPendingCount = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/payable/fetchPayableSearch`, {
        params: {
          status: "Pending",
          searchText: "",
          currencyId: "All",
          limit: 1,
          page: 1,
        },
      });
      setPendingCount(res.data.totalItems ?? 0);
    } catch (error) {
      console.error("Failed to fetch pending count:", error);
    }
  };

  useEffect(() => {
    fetchAllPendingCount();
  }, []);

  const [widgetData, setWidgetData] = useState({
    lastCutoffPayable: 0,
    currentTotalPayable: 0,
    currentCutoffPayable: 0,
    totalDiscount: 0,
    totalReOpen: 0,
    totalIssued: 0,
    previousPurchaseKilo: 0,
    currentPurchaseKilo: 0,
  });

  const payableTotalPages = localStorage.getItem("Payable");
  const validPage = useValidPage(payableTotalPages);

  const pagination = useServerPagination(null, 10, {}, validPage);
  const [payable, setPayable] = useState([]);
  const [cutoff, setCutoff] = useState("");
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filterColumn, setFilterColumn] = useState("all");
  const [selectedVendor, setSelectedVendor] = useState([]);
  const [selectedCutOff, setSelectedCutOff] = useState({
    id: "All",
    from: null,
    to: null,
  });
  const [cutOffs, setCutOffs] = useState([]);
  const [deletionTrigger, setDeletionTrigger] = useState(false);

  const [currencyId, setCurrencyId] = useState(() =>
    FilterStore.getCurrencyId(),
  );
  useEffect(() => {
    FilterStore.saveCurrencyId(currencyId);
  }, [currencyId]);

  const [widgetCurrencySymbol, setWidgetCurrencySymbol] = useState(() =>
    FilterStore.getCurrencyName(),
  );

  useEffect(() => {
    FilterStore.saveCurrencyName(widgetCurrencySymbol);
  }, [widgetCurrencySymbol]);

  const [vendorFetch, setVendorFetch] = useState([]);
  const [showReturnItemsModal, setShowReturnItemsModal] = useState(false);
  const [itemsToReturn, setItemsToReturn] = useState([]);
  const [searchTermFilter, setSearchTermFilter] = useState("");
  const debounceTimer = useRef(null);
  const [selectedItems, setSelectedItems] = useState(new Set());

  const reloadVendorFetch = () => {
    axios
      .get(BASE_URL + "/vendors/fetchVendors")
      .then((res) => {
        setVendorFetch(res.data);
      })
      .catch((err) => {
        // console.log(err);
      });
  };

  const fetchWidgets = async (cutoffParam) => {
    // Always call the API — even with no date range (Clear Filter / "show all").
    // The backend now falls back to the real earliest/latest purchase date and
    // returns real all-time totals when startDate/endDate are omitted, instead
    // of us zeroing the widgets out here on the client before ever asking it.
    try {
      const res = await axios.get(`${BASE_URL}/payable/widgets/purchase`, {
        params: {
          startDate: cutoffParam?.from,
          endDate: cutoffParam?.to,
          currencyId: currencyId,
        },
      });
      const data = res.data;
      setWidgetData({
        lastCutoffPayable: data.lastCutoffPayable || 0,
        currentTotalPayable: data.currentTotalPayable || 0,
        currentCutoffPayable: data.currentCutoffPayable || 0,
        totalDiscount: data.totalDiscount || 0,
        totalReOpen: data.reOpen || 0,
        totalIssued: data.totalTotalPaidPurchase || 0,
        previousPurchaseKilo: data.previousPurchaseKilo || 0,
        currentPurchaseKilo: data.currentPurchaseKilo || 0,
      });
    } catch (err) {
      console.error("Error fetching widgets:", err);
    }
  };

  // handle change select cutoff date
  const handleSelect = (e) => {
    const selectedValue = e.target.value;

    if (selectedValue === "All") {
      const earliestFrom = new Date(
        Math.min(...cutOffs.map((item) => new Date(item.from).getTime())),
      );

      const latestTo = new Date(
        Math.max(...cutOffs.map((item) => new Date(item.to).getTime())),
      );

      const allCutoff = { id: "All", from: earliestFrom, to: latestTo };

      setSelectedCutOff(allCutoff);
      fetchWidgets(allCutoff);
    } else {
      const selectedItem = cutOffs.find((item) => item.id === selectedValue);

      if (selectedItem) {
        const cutoff = {
          id: selectedItem.id,
          from: selectedItem.from,
          to: selectedItem.to,
        };

        setSelectedCutOff(cutoff);
        fetchWidgets(cutoff);
      }
    }
  };

  const fetchCutOff = () => {
    reloadVendorFetch();
    // Called after delete / bulk approve-reject, so keep the badge in sync
    fetchAllPendingCount();

    // axios
    //   .get(BASE_URL + "/cutoff/getCutoffs")
    //   .then((res) => {
    //     setCutOffs(res.data);

    //     const latestCutoff = res.data.reduce((latest, current) =>
    //       new Date(current.to) > new Date(latest.to) ? current : latest
    //     );

    //     setSelectedCutOff({
    //       id: latestCutoff.id,
    //       from: new Date(latestCutoff.from),
    //       to: new Date(latestCutoff.to),
    //     });
    //

    ///PARA ITO SA FILTER RETENTION
    ///START

    // If user previously clicked Clear Filter, PAisAllData() is true.
    // Stay in "show all" mode instead of falling back to current month.
    if (FilterStore.PAisAllData()) {
      const allCutoff = { id: "All", from: null, to: null };
      setSelectedCutOff(allCutoff);
      fetchWidgets(allCutoff);
      const boundaries = getMonthBoundaries();
      const defaultCutoff = initializeCutoff(boundaries);
      setDisplayCutOff({ from: defaultCutoff.from, to: defaultCutoff.to });
      return;
    }

    const savedFilter = FilterStore.get();

    // Use saved filter if available, otherwise fall back to current month default
    const initialCutoff =
      savedFilter ??
      (() => {
        const boundaries = getMonthBoundaries();
        return initializeCutoff(boundaries);
      })();
    ///END

    setSelectedCutOff({
      id: initialCutoff.id,
      from: initialCutoff.from,
      to: initialCutoff.to,
    });
    setDisplayCutOff({ from: initialCutoff.from, to: initialCutoff.to });

    fetchWidgets(initialCutoff);
    // })
    // .catch((err) => {
    //   console.log(err);
    // });
  };

  useEffect(() => {
    fetchCutOff();
  }, [currencyId]);

  const columns = [
    {
      name: <div className="text-nowrap">Transaction Date</div>,
      sortField: "createdAt",
      selector: (row) => format(row.createdAt, "MMM/dd/yyyy, hh:mm a"),
      cell: (row) => (
        <div className="text-nowrap text-center">
          <NoTranslate>
            {format(row.createdAt, "MMM/dd/yyyy, hh:mm a")}
          </NoTranslate>
        </div>
      ),
    },
    // {
    //   name: "Transaction No.",
    //   selector: (row) => row.client_transaction_id,
    //   cell: (row) => (
    //     <div className="text-nowrap text-center">
    //       {row.client_transaction_id}
    //     </div>
    //   ),
    // },
    {
      name: "Transaction No.",
      sortField: "client_transaction_id",
      selector: (row) => row.client_transaction_id,
      cell: (row) => (
        <div className="text-nowrap text-center">
          <NoTranslate>{row.client_transaction_id}</NoTranslate>
        </div>
      ),
      conditionalCellStyles: [
        {
          when: (row) =>
            row.client_transaction_id &&
            row.client_transaction_id.startsWith("ROT-"),
          style: {
            color: "#16c03d",
          },
        },
      ],
    },
    {
      name: "Receiving Warehouse",
      sortField: "warehouse.name",
      selector: (row) => row?.warehouse?.name,
      cell: (row) => (
        <div className="text-center">
          {" "}
          <NoTranslate>{row?.warehouse?.name}</NoTranslate>
        </div>
      ),
    },
    {
      name: "Mode of Payment",
      sortField: "MOP",
      selector: (row) =>
        row.MOP === null ? (
          <div className="text-center">No Payment Chosen</div>
        ) : (
          <div className="text-center">
            <NoTranslate>{row.MOP}</NoTranslate>
          </div>
        ),
    },
    {
      name: "Supplier",
      sortField: "vendor.company_name",
      selector: (row) => (
        <div className="text-center">
          {" "}
          <NoTranslate>{row?.vendor?.company_name}</NoTranslate>
        </div>
      ),
    },
    {
      name: "Discount",
      sortField: "discount_value",
      selector: (row) => (
        <div className="text-center">{row.discount_value}</div>
      ),
    },
    {
      name: "Container Number",
      sortField: "container_number",
      selector: (row) => row.container_number,
      cell: (row) => (
        <div
          className="text-center"
          title={row?.container_number?.length > 25 && row.container_number}
        >
          <NoTranslate>
            {truncateText(row?.container_number, 25) || "n/a"}
          </NoTranslate>
        </div>
      ),
    },
    {
      name: "Pier",
      sortField: "pier",
      selector: (row) => row.pier,
      cell: (row) => (
        <div className="text-center" title={row?.pier?.length > 25 && row.pier}>
          <NoTranslate>{truncateText(row?.pier, 25) || "n/a"}</NoTranslate>
        </div>
      ),
    },
    {
      name: "Destination",
      sortField: "domestic_type",
      selector: (row) => row.domestic_type,
      cell: (row) => (
        <div className="text-center">
          <NoTranslate>
            {row.domestic_type === "local" ? "Local" : "Overseas"}
          </NoTranslate>
        </div>
      ),
    },
    {
      name: "Deadline",
      sortField: "due_date",
      selector: (row) => (
        <div className="text-center">
          <NoTranslate>
            {row.due_date ? format(row.due_date, "MMM/dd/yyyy") : "TBA"}
          </NoTranslate>
        </div>
      ),
    },
    {
      name: "Purchase Date",
      sortField: "purchaseDate",
      selector: (row) => (
        <div className="text-center">
          <NoTranslate>{format(row.purchaseDate, "MMM/dd/yyyy")}</NoTranslate>
        </div>
      ),
    },
    {
      name: "Date Approved",
      sortField: "date_approved",
      selector: (row) => (
        <div className="text-center">
          <NoTranslate>
            {row.date_approved
              ? format(row.date_approved, "MMM/dd/yyyy hh:mm a")
              : "n/a"}
          </NoTranslate>
        </div>
      ),
    },

    //commented kasi meron na sa payable ang total price or amount
    // {
    //   name: "Total Amount",
    //   cell: (row) => {
    //     let unitPrice = 0;

    //     row?.payable_products.forEach((data) => {
    //       unitPrice += data.unitPrice * data.weight;
    //     });

    //     const moisture = row?.payable_products.reduce((acc, data) => {
    //       if (data.moisture_type === "%") {
    //         return (
    //           acc +
    //           parseFloat(
    //             (data.moisture / 100) * data.unitPrice * data.weight || 0
    //           )
    //         );
    //       } else {
    //         return acc + parseFloat(data.moisture || 0);
    //       }
    //     }, 0);

    //     const totalOtherFees = row.payable_other_fees.reduce(
    //       (acc, data) => acc + parseFloat(data.fee_amount || 0),
    //       0
    //     );

    //     const calculateDiscount =
    //       row.isPercent_Discount === true
    //         ? (row.discount_value / 100) *
    //           parseFloat(
    //             unitPrice - moisture - row.weighing_fee - totalOtherFees
    //           )
    //         : row.discount_value;

    //     return (
    //       <span className="text-center">
    //         {row.currency?.currency_name}{" "}
    //         {(
    //           unitPrice -
    //           moisture -
    //           row.weighing_fee -
    //           totalOtherFees -
    //           calculateDiscount
    //         ).toLocaleString("en-US", {
    //           minimumFractionDigits: 2,
    //           maximumFractionDigits: 2,
    //         })}
    //       </span>
    //     );
    //   },
    // },
    // {
    //   name: "Total Amount",
    //   selector: (row) => (
    //     <div className="text-center">{`${
    //       row.currency?.currency_name
    //     } ${row.totalPrice.toLocaleString("en-US", {
    //       minimumFractionDigits: 2,
    //       maximumFractionDigits: 2,
    //     })}`}</div>
    //   ),
    // },
    // Created a conditional styling
    {
      name: "Total Amount",
      sortField: "totalPrice",
      selector: (row) => row.totalPrice,
      cell: (row) => {
        const isROT =
          row.client_transaction_id &&
          row.client_transaction_id.startsWith("ROT-");
        const amount = parseNumber(row.totalPrice) || 0;

        if (isROT) {
          const isNegative = amount < 0;
          const isPositive = amount > 0;

          return (
            <div
              className="text-center"
              style={{
                color: isNegative ? "red" : isPositive ? "green" : "inherit",
                fontWeight: "bold",
              }}
            >
              {isPositive ? "+" : ""}
              {formatCurrency(amount, row.currency?.currency_name)}
            </div>
          );
        }

        return (
          <div className="text-center">
            <NoTranslate>
              {formatCurrency(amount, row.currency?.currency_name)}
            </NoTranslate>
          </div>
        );
      },
    },
    {
      name: "Status",
      sortField: "status",
      selector: (row) => row.status,
      cell: (row) => {
        let fontColor;

        switch (row.status) {
          case "Pending":
            fontColor = "#FFA500";
            break;
          case "Approved":
            fontColor = "#3B9F3F";
            break;
          case "Paid":
            fontColor = "#3B9F3F";
            break;
          case "Rejected":
            fontColor = "#FF0000";
            break;
          default:
            fontColor = "#000000"; // Default color
            break;
        }
        return (
          <div
            style={{
              padding: "5px 10px",
              borderRadius: "5px",
              color: fontColor,
              textTransform: "uppercase",
              fontWeight: "bold",
            }}
            className="text-center"
          >
            {" "}
            <NoTranslate>{row.status}</NoTranslate>
          </div>
        );
      },
    },
  ];

  if (authrztn.includes("Payable-Delete")) {
    // CHANGED: Action column now uses the ActionMenu component (3-dot kebab menu)
    // instead of the previous inline trash icon and return button.
    // Handler props are passed down so ActionMenu can trigger the same actions.
    columns.push({
      name: "Action",
      cell: (row) => (
        <ActionMenu
          row={row}
          handleDeletePayable={handleDeletePayable}
          getItemsToReturn={getItemsToReturn}
          setShowReturnItemsModal={setShowReturnItemsModal}
        />
      ),
    });
  }

  const handleDeletePayable = async (
    primary_id,
    purchase_date,
    transaction_id,
  ) => {
    swal({
      title: "Confirm Deletion",
      text: "Are you sure you want to delete?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          const response = await axios.delete(
            `${BASE_URL}/payable/deletePayable/`,
            {
              params: {
                primary_id,
                purchase_date,
                transaction_id,
                userLoggedID,
              },
            },
          );
          if (response.status === 200) {
            swal({
              title: "Payable Deleted Successfully!",
              text: "The payable has been successfully deleted.",
              icon: "success",
              button: "OK",
            }).then(() => {
              fetchCutOff();
              setDeletionTrigger(true);
            });
          } else if (response.status === 201) {
            swal({
              title: "Delete Prohibited!",
              text: `Payable date (${response.data.purchase_date}) cannot be deleted as its payable date falls within the posted cutoff period named "${response.data.cutoff_name}".`,
              icon: "warning",
              button: "OK",
            });
          } else if (response.status === 202) {
            const { transactionNumber, moduleType } = response.data;
            let moduleFrom;
            if (moduleType === "local") {
              moduleFrom = "Local Purchase";
            } else {
              moduleFrom = "Overseas Purchase";
            }
            const title = document.createElement("div");
            const text = document.createElement("div");
            const swalInfo = document.createElement("div");

            title.innerHTML = `<div style="font-size: 1.5rem; font-weight: bold;">Deletion Prohibited!</div>`;
            text.innerHTML = `<div style="text-align: center; margin-top: 10px;">
                              Delete first the purchase in module <strong>${moduleFrom}</strong> with 
                              Transaction Number: <strong>${transactionNumber}</strong>
                            </div>`;

            swalInfo.append(title);
            swalInfo.append(text);
            swal({
              icon: "error",
              content: swalInfo,
            });
          } else if (response.status === 203) {
            const { transactionNumber } = response.data;

            const title = document.createElement("div");
            const text = document.createElement("div");
            const swalInfo = document.createElement("div");

            title.innerHTML = `<div style="font-size: 1.5rem; font-weight: bold;">Deletion Prohibited!</div>`;
            text.innerHTML = `<div style="text-align: center; margin-top: 10px;">
                              Delete first the production in module <strong>${`Productions`}</strong> with 
                              Transaction Number: <strong>${transactionNumber}</strong>
                            </div>`;

            swalInfo.append(title);
            swalInfo.append(text);
            swal({
              icon: "error",
              content: swalInfo,
            });
          } else if (response.status === 207) {
            const { transactionNumber } = response.data;
            // console.log(response.data);
            const title = document.createElement("div");
            const text = document.createElement("div");
            const swalInfo = document.createElement("div");

            title.innerHTML = `<div style="font-size: 1.5rem; font-weight: bold;">Deletion Prohibited!</div>`;
            text.innerHTML = `<div style="text-align: center; margin-top: 10px;">
                              Delete first the sales in module <strong>${`Sales Invoice`}</strong> with 
                              Transaction Number: <strong>${transactionNumber}</strong>
                            </div>`;

            swalInfo.append(title);
            swalInfo.append(text);
            swal({
              icon: "error",
              content: swalInfo,
            });
          } else if (response.status === 208) {
            const { transactionNumber } = response.data;
            // console.log(response.data);
            const title = document.createElement("div");
            const text = document.createElement("div");
            const swalInfo = document.createElement("div");

            title.innerHTML = `<div style="font-size: 1.5rem; font-weight: bold;">Deletion Prohibited!</div>`;
            text.innerHTML = `<div style="text-align: center; margin-top: 10px;">
                              Delete first the stock transfered in module <strong>${`Stock Transfer`}</strong> with
                              Transaction Number: <strong>${transactionNumber}</strong>
                            </div>`;

            swalInfo.append(title);
            swalInfo.append(text);
            swal({
              icon: "error",
              content: swalInfo,
            });
          } else if (response.status === 209) {
            // Commission expense is already added to a bulk expense payment
            const { transactionNumber } = response.data;
            const swalInfo = document.createElement("div");
            swalInfo.innerHTML = `<div style="font-size: 1.5rem; font-weight: bold;">Deletion Prohibited!</div>
                            <div style="text-align: center; margin-top: 10px;">
                              The commission expense <strong>${transactionNumber}</strong> is already added to a payment in module <strong>Expenses</strong>. Remove it from the payment first.
                            </div>`;
            swal({
              icon: "error",
              content: swalInfo,
            });
          } else {
            swal({
              icon: "error",
              title: "Something went wrong",
              text: "Please contact our support team for assistance.",
            });
          }
        } catch (error) {
          if (error.response.status === 401) {
            swal({
              icon: "error",
              // title: "Please contact your support",
              title: error.response.data.title,
              text: error.response.data.message,
            }); // test 1
          } else {
            //console.log(error);
            swal({
              icon: "error",
              title: "Error",
              text: "An error occurred while deleting the invoice.",
            });
          }
        }
      }
    });
  };

  const getLatestCutoff = () => {
    axios
      .get(BASE_URL + "/payable/getLatestCutoff")
      .then((res) => {
        setCutoff(res.data?.name);
      })
      .catch((err) => {
        //console.log(err);
        setIsLoading(true);
      });
  };

  const getItemsToReturn = (payableId, payableTransactionNumber) => {
    try {
      backloadPagination.updateParams({
        payableId,
        payableTransactionNumber,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleReturnProduct = async (product) => {
    try {
      const res = await axios.post(
        `${BASE_URL}/payable/backload/return-product`,
        {
          product,
          userLoggedID,
        },
      );

      if (res.status === 200) {
        swal({
          icon: "success",
          title: "Return Successful",
          text: "Product has been returned.",
          timer: 2000,
          buttons: false,
        }).then(() => {
          getItemsToReturn(product.payableId, product.payableTransactionNumber);
          setRefreshKey((prev) => prev + 1);
        });
      }
    } catch (error) {
      if (error.response && error.response.status === 403) {
        swal({
          icon: "warning",
          title: error.response.data.title,
          text: error.response.data.message,
        });
        return;
      }

      if (error.response && error.response.status === 409) {
        const totalStock = error.response.data.totalStock;
        const wrapper = document.createElement("div");
        wrapper.classList.add("center-swal-text");
        wrapper.innerHTML = `The return quantity exceeds the available stock <strong>(${formatToTwoDecimal(totalStock)})</strong>`;
        swal({
          icon: "error",
          title: "Invalid Quantity To Return",
          content: wrapper,
        });
        return;
      }

      if (error.response && error.response.status === 400) {
        swal({
          icon: "error",
          title: error.response.data.title,
          text: error.response.data.message,
        });
      }

      console.error(error);
    }
  };

  const toggleItemSelection = (index) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });
  };

  const handleBulkReturn = () => {
    if (selectedItems.size === 0) {
      swal({
        icon: "warning",
        title: "No Items Selected",
        text: "Please select at least one item to return.",
      });
      return;
    }

    const itemsWithValidQuantity = Array.from(selectedItems).filter((index) => {
      const item = itemsToReturn[index];
      return parseNumber(item.quantityToReturn) > 0;
    });

    if (itemsWithValidQuantity.length === 0) {
      swal({
        icon: "warning",
        title: "Invalid Quantities",
        text: "All selected items must have a return quantity greater than zero.",
      });
      return;
    }

    swal({
      icon: "warning",
      title: "Confirm Product Return",
      text: `Are you sure you want to return ${itemsWithValidQuantity.length} product(s)? This action cannot be undone.`,
      buttons: ["Cancel", "Yes, Return"],
      dangerMode: true,
    }).then(async (confirm) => {
      if (confirm) {
        for (const index of itemsWithValidQuantity) {
          await handleReturnProduct(itemsToReturn[index]);
        }
      }
    });
  };

  const handleCloseReturnItems = () => {
    setShowReturnItemsModal(false);
    setSelectedItems(new Set());
  };

  const vendorDataFilter = vendorFetch.map((option) => ({
    value: option.id,
    label: option.company_name || option.first_name + " " + option.last_name,
  }));

  const widgetInfo = {
    "Last cut-off Payable": {
      formula: "Σ Debit − Σ Credit (PayableJournal)",
      conditions: [
        "Journal date is before the start of the selected cutoff period",
        "Filtered by selected currency (or all currencies × rate)",
      ],
      description:
        "The outstanding payable balance carried over before the current cutoff period begins. This is your opening payable balance.",
    },
    "Current Total Payable": {
      formula: "Σ Debit (PayableJournal)",
      conditions: [
        "Journal date is within the selected date range",
        "Filtered by selected currency (or all currencies × rate)",
      ],
      description:
        "Total value of all payables within the selected period, based on debit entries in the payable journal.",
    },
    "Total Discount": {
      formula: "Σ discount_value (Payable)",
      conditions: [
        "Purchase Date is within the selected date range",
        "isPercent_Discount = false (flat-amount discount)",
        "isPercent_Discount = true (converted percentage to flat-amount-equivalent discount)",
        "Status: Approved, Partially-Paid, or Paid",
        "Filtered by selected currency (or all currencies × rate)",
      ],
      description:
        "Sum of all discounts applied to approved payables within the period.",
    },
    "Re-Open": {
      formula: "Σ total_amount (PayableJournal, ROT-PO entries only)",
      conditions: [
        "transaction_number starts with 'ROT-PO' (e.g. ROT-PO-20260410582449)",
        "date is within the selected date range",
      ],
      description:
        "Net amount of re-opened purchase orders in the period. A re-open reverses a previously closed payable, creating new journal entries prefixed with ROT-PO.",
    },
    "Current cut-off Payable": {
      formula: "Σ Debit − Σ Credit (PayableJournal)",
      conditions: [
        "Journal date is within the selected date range",
        "Filtered by selected currency (or all currencies × rate)",
      ],
      description:
        "Net payable balance for the current cutoff period — the total of all approved purchases within the date range, representing what is still owed to suppliers this period.",
    },
    "Total Paid Purchase": {
      formula: "Σ Credit (PayableJournal)",
      conditions: [
        "Journal date is within the selected date range",
        "Filtered by selected currency (or all currencies × rate)",
      ],
      description:
        "Total payment amounts already issued to suppliers within the selected period. Represents checks or transfers that have been Paid.",
    },
    "Previous Month Purchase Kilo": {
      formula: "Σ net_weight (Payable_Product, previous cutoff period)",
      conditions: [
        "Purchase date is within the previous cutoff period",
        "Status: Approved, Partially-Paid, or Paid",
        "Filtered by selected currency",
      ],
      description:
        "Total weight (kg) of goods purchased in the previous cutoff period. Used as a baseline reference to compare against the current month's volume.",
    },
    "Current Month Purchase Kilo": {
      formula: "Σ net_weight (Payable_Product, current date range)",
      conditions: [
        "Purchase date is within the selected date range",
        "Status: Approved, Partially-Paid, or Paid",
        "Filtered by selected currency",
      ],
      description:
        "Total weight (kg) of goods purchased within the current selected period.",
    },
  };

  const widgetList = [
    {
      // Column 1
      widgetOneTitle: "Last cut-off Payable",
      widgetOneAmount: widgetData.lastCutoffPayable,
      // Column 2
      widgetTwoTitle: "Current Total Payable",
      widgetTwoAmount: widgetData.currentTotalPayable,
    },
    {
      // Column 1
      widgetOneTitle: "Total Discount",
      widgetOneAmount: widgetData.totalDiscount,
      // Column 2
      widgetTwoTitle: "Re-Open",
      widgetTwoAmount: widgetData.totalReOpen,
    },
    {
      // Column 1
      widgetOneTitle: "Current cut-off Payable",
      widgetOneAmount: widgetData.currentCutoffPayable,
      // Column 2
      widgetTwoTitle: "Total Paid Purchase",
      widgetTwoAmount: widgetData.totalIssued,
    },
    {
      // Column 1
      widgetOneTitle: "Previous Month Purchase Kilo",
      widgetOneAmount: widgetData.previousPurchaseKilo,

      widgetTwoTitle: "Current Month Purchase Kilo",
      widgetTwoAmount: widgetData.currentPurchaseKilo,
    },
  ];

  const [clearFilterSignal, setClearFilterSignal] = useState(0);
  const [datePickerKey, setDatePickerKey] = useState(0);
  const [displayCutOff, setDisplayCutOff] = useState({ from: null, to: null });

  const handleClearFilter = () => {
    // Mark "show all" in localStorage so remount also fetches without date filter.
    FilterStore.PAsaveAllData();
    FilterStore.save(null, null); // clear any saved date range

    const allCutoff = { id: "All", from: null, to: null };
    setSelectedCutOff(allCutoff); // null dates — fetching shows all data
    fetchWidgets(allCutoff);

    // Reset picker display to default month boundaries — does NOT affect fetching.
    const boundaries = getMonthBoundaries();
    const defaultCutoff = initializeCutoff(boundaries);
    setDisplayCutOff({ from: defaultCutoff.from, to: defaultCutoff.to });
    setDatePickerKey((n) => n + 1);

    // Signal child tabs to reset their search/vendor/filterColumn state.
    setClearFilterSignal((n) => n + 1);
  };

  const statusProps = {
    selectedCutOff,
    currencyId,
    setSelectedCutOff,
    vendorDataFilter,
    setSearchTermFilter,
    columns,
    clearFilterSignal, // incremented by handleClearFilter so child tabs reset search/vendor/column
    authrztn,
    userLoggedID,
    // After bulk approve/reject: refetch tables, widgets and pending badge
    onBulkDone: () => {
      setRefreshKey((prev) => prev + 1);
      fetchCutOff();
    },
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      getLatestCutoff();
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (backloadPagination.data) {
      setItemsToReturn(
        backloadPagination.data.map((item) => {
          const payableId = item.payable_id;
          const payableTransactionNumber = item.payable.transaction_id;
          const productId = item.product_tag_vendor.product_list.product_id;
          const productCode = item.product_tag_vendor.product_list.product_code;
          const productName = item.product_tag_vendor.product_list.product_name;
          const weight = item.weight;
          const moisture = item.moisture;
          const unitPrice = item.unitPrice;
          const netWeight = item.net_weight;
          const static_netWeight = item.static_net_weight;
          return {
            payableId,
            payableTransactionNumber,
            productId,
            productCode,
            productName,
            quantity: weight,
            moisture,
            quantityToReturn: "",
            unitPrice,
            netWeight,
            static_netWeight,
          };
        }),
      );
      setSelectedItems(new Set());
    }
  }, [backloadPagination.data]);

  // Fetch vendors when searchTerm changes (debounced)
  useEffect(() => {
    if (searchTermFilter.trim() === "") {
      reloadVendorFetch();
      return;
    }

    // Clear previous debounce timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new debounce timer
    debounceTimer.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(BASE_URL + "/vendors/fetchVendors", {
          params: { search: searchTermFilter, limit: 5 },
        });
        setVendorFetch(data);
        //console.log("Fetched vendors:", data);
      } catch (error) {
        console.error(error);
      }
    }, 600);

    // Cleanup timer on unmount or searchTermFilter change
    return () => clearTimeout(debounceTimer.current);
  }, [searchTermFilter]);

  // "pdf" | "excel" while the export options modal is open, else null
  const [exportFormat, setExportFormat] = useState(null);
  const pdfPreview = usePdfPreview();

  const handleDateRangeChange = (startDate, endDate) => {
    const cutoff = {
      id: "custom",
      from: startDate,
      to: endDate,
    };
    setSelectedCutOff(cutoff);
    setDisplayCutOff({ from: startDate, to: endDate });
    fetchWidgets(cutoff);
    FilterStore.save(startDate, endDate); /// FILTER RETENTION
    FilterStore.PAclearAllData(); // user picked a real date, exit "show all" mode
  };

  return (
    <div className="h-100 w-100 border bg-white custom-container">
      {isLoading ? (
        <div className="loading-container">
          <ThreeDot
            variant="brick-stack"
            color="#6290FE"
            size="large"
            text="Loading Data..."
            textColor=""
          />
        </div>
      ) : authrztn.includes("Payable-View") ? (
        <>
          <div className="w-100 p-2 mb-2 d-flex flex-row justify-content-between">
            <div className="d-flex flex-column title-custom">
              <span className="fs-3">
                PAYABLE
                {/* -- {cutoff} */}
              </span>
            </div>

            <div className="d-flex flex-row">
              {/* Export Pdf and excel */}
              <div className="btn-group">
                <button
                  type="button"
                  className="btn btn-success dropdown-toggle pt-2 px-3 me-2"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  Export
                </button>
                <ul className="dropdown-menu">
                  <li
                    className="dropdown-item border-bottom"
                    style={{ cursor: "pointer" }}
                    onClick={() => setExportFormat("pdf")}
                  >
                    PDF
                  </li>
                  <li
                    className="dropdown-item"
                    style={{ cursor: "pointer" }}
                    onClick={() => setExportFormat("excel")}
                  >
                    Excel
                  </li>
                </ul>
              </div>

              {/* With authorization Re-Open Tickets */}
              {/* <div>
                    {authrztn.includes("In Progress") && (
                    <PayableReOpenModal
                      selectedCutOff={selectedCutOff}
                      currencyId={currencyId}
                      handleDateRangeChange={handleDateRangeChange}  
                    />)} 
              </div>
              */}

              {/* Purchase Re-Open Ticket */}
              {userLoggedID === "11111111-1111-1111-1111-111111111111" && (
                <PayableReOpenModal
                  selectedCutOff={selectedCutOff}
                  currencyId={currencyId}
                  handleDateRangeChange={handleDateRangeChange}
                />
              )}

              <div>
                {authrztn.includes("Payable-Add") && (
                  <Link
                    to="/purchases/create_payable"
                    className="btn btn-primary d-flex flex-row align-items-center title-button"
                  >
                    <i className="bx bx-plus fs-5"></i> Create
                  </Link>
                )}
              </div>
            </div>
          </div>
          <CurrencySelector
            setWidgetCurrencySymbol={setWidgetCurrencySymbol}
            currencyId={currencyId}
            setCurrencyId={setCurrencyId}
            style={{ maxWidth: "8.5rem" }}
          />
          <div className="container-fluid position-relative">
            {/* Widgets */}
            <div className="mt-3 mb-4">
              {widgetList.map((item, index) => (
                <div className="d-flex mb-3" key={index}>
                  {/* Widget Column #1 */}
                  <div className="col-sm w-100 ps-3 pe-2 payable-card">
                    <div
                      className="d-flex align-items-center justify-content-between w-100 p-3 border shadow-sm rounded hover-card"
                      style={{ transition: "transform 0.2s, box-shadow 0.2s" }}
                    >
                      <div className="d-flex flex-row align-items-center gap-2">
                        <i className="bx bx-bar-chart-alt fs-4 text-secondary"></i>
                        <h3 className="fs-6 mb-0 text-secondary">
                          {item.widgetOneTitle}
                          <WidgetInfoIcon
                            title={item.widgetOneTitle}
                            widgetInfo={widgetInfo}
                          />
                        </h3>
                      </div>

                      <div className="d-flex align-items-center flex-column payable-card-desc">
                        <h1
                          className="text-nowrap fs-5 mb-0 fw-bold"
                          style={{
                            cursor: "pointer",
                            color: "var(--widget-number-color)",
                          }}
                          title={formatToTwoDecimal(item.widgetOneAmount)}
                        >
                          {!item.widgetOneTitle?.includes("Kilo") &&
                            widgetCurrencySymbol}
                          {formatToTwoDecimal(item.widgetOneAmount)}
                        </h1>
                      </div>
                    </div>
                  </div>

                  {/* Widget Column #2 */}
                  <div
                    className={`col-sm w-100 px-2 payable-card ${
                      !item.widgetTwoTitle && "d-none"
                    }`}
                  >
                    <div
                      className="d-flex align-items-center justify-content-between w-100 p-3 border shadow-sm rounded hover-card"
                      style={{ transition: "transform 0.2s, box-shadow 0.2s" }}
                    >
                      <div className="d-flex gap-2 flex-row align-items-center">
                        <i className="bx bx-wallet fs-4 text-secondary"></i>
                        <h3 className="fs-6 mb-0 text-secondary">
                          {item.widgetTwoTitle}
                          <WidgetInfoIcon
                            title={item.widgetTwoTitle}
                            widgetInfo={widgetInfo}
                          />
                        </h3>
                      </div>

                      <div>
                        <h1
                          className="text-nowrap fs-5 mb-0 fw-bold"
                          style={{
                            cursor: "pointer",
                            color: "var(--widget-number-color)",
                          }}
                          title={formatToTwoDecimal(item.widgetTwoAmount)}
                        >
                          {!item.widgetTwoTitle?.includes("Kilo") &&
                            widgetCurrencySymbol}
                          {formatToTwoDecimal(item.widgetTwoAmount)}
                        </h1>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Cutoff filter */}
            {activeTab === "approved" && (
              <div className="d-flex flex-row align-items-end gap-2 mt-2 mb-2">
                <DateRangePicker
                  key={datePickerKey}
                  startDate={displayCutOff?.from}
                  endDate={displayCutOff?.to}
                  // startDate={selectedCutOff?.from}
                  //   endDate={selectedCutOff?.to}
                  onDateRangeChange={handleDateRangeChange}
                  label="Date Range"
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary d-flex align-items-center gap-1"
                  onClick={handleClearFilter}
                  title="Reset date range and reload all data"
                >
                  <i className="bx bx-x-circle"></i>
                  Clear Filter
                </button>
                {/* <label htmlFor="vendor">Cutofaf</label>
                <select
                  value={selectedCutOff?.id}
                  onChange={handleSelect}
                  className="form-select"
                  onMouseDown={(e) => {
                    if (cutOffs.length === 0) {
                      e.preventDefault();
                      swal({
                        icon: "warning",
                        title: "No Cutoff Found",
                        text: "No cutoff records found. Please create a cutoff first in Monthly Cutoff Module.",
                      });
                      return;
                    }
                  }}
                >
                  <option value="All">All</option>
                  {cutOffs.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select> */}
                {/* </div> */}
                {/* <div className="col-sm mb-2">
                <label htmlFor="fromDate">From</label>
                <DatePicker
                  selected={selectedCutOff?.from}
                  dateFormat="MMM/dd/yyyy"
                  className="form-control"
                  readOnly
                />
              </div>
              <div className="col-sm mb-2">
                <label htmlFor="toDate">To</label>
                <DatePicker
                  selected={selectedCutOff?.to}
                  dateFormat="MMM/dd/yyyy"
                  className="form-control"
                  readOnly
                />
              </div> */}
                {/* <div className="col-sm d-flex flex-row align-items-end mb-2 filter-btn-container w-100"></div>
              <div className="col-sm"></div> */}
              </div>
            )}
            {/* Status tabs */}
            <div className="container-fluid mt-3">
              <RefreshContext.Provider value={refreshKey}>
                <Tabs
                  transition={false}
                  mountOnEnter
                  unmountOnExit={false}
                  activeKey={activeTab} ///FILTER RETENTION
                  onSelect={handleTabSelect} ///FILTER RETENTION
                  id="status-tabs"
                  className="mb-3"
                >
                  <Tab
                    eventKey="pending"
                    title={
                      <span>
                        Pending{" "}
                        {pendingCount > 0 && (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "#000",
                              color: "#fff",
                              borderRadius: "999px",
                              fontSize: "0.7rem",
                              fontWeight: "bold",
                              minWidth: "1.4em",
                              height: "1.4em",
                              padding: "0 0.4em",
                              lineHeight: 1,
                              verticalAlign: "middle",
                              marginLeft: "4px",
                            }}
                          >
                            {pendingCount}
                          </span>
                        )}
                      </span>
                    }
                  >
                    <PendingStatusTab {...statusProps} />
                  </Tab>
                  <Tab eventKey="approved" title="Approved">
                    <ApprovedStatusTab {...statusProps} />
                  </Tab>
                </Tabs>
              </RefreshContext.Provider>
            </div>
          </div>
        </>
      ) : (
        <div className="no-access">
          <img src={NoAccess} alt="NoAccess" className="no-access-img" />
          <h3>You don't have access to this function.</h3>
        </div>
      )}

      {/* PO Backload Modal */}
      <Modal
        show={showReturnItemsModal}
        onHide={handleCloseReturnItems}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-primary fw-bold">Backload</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="overflow-auto">
            <div
              style={{ maxHeight: "80vh", overscrollBehaviorBlock: "contain" }}
              className="overflow-auto"
            >
              <table className="table table-bordered table-striped table-responsive text-center text-nowrap">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedItems(
                              new Set(itemsToReturn.map((_, i) => i)),
                            );
                          } else {
                            setSelectedItems(new Set());
                          }
                        }}
                        checked={
                          selectedItems.size === itemsToReturn.length &&
                          itemsToReturn.length > 0
                        }
                      />
                    </th>
                    <th>Product Code</th>
                    <th>Product Name</th>
                    <th>Quantity</th>
                    <th>Moisture</th>
                    <th>Net Weight</th>
                    <th>Quantity to Return</th>
                    <th>Returned</th>
                  </tr>
                </thead>
                <tbody>
                  <TransitionGroup component={null}>
                    {itemsToReturn.map((item, index) => (
                      <CSSTransition
                        key={index}
                        timeout={300}
                        classNames="table-row"
                      >
                        <tr>
                          <td className="align-middle">
                            <input
                              type="checkbox"
                              checked={selectedItems.has(index)}
                              onChange={() => toggleItemSelection(index)}
                            />
                          </td>
                          <td className="align-middle">{item.productCode}</td>
                          <td className="align-middle">{item.productName}</td>
                          <td className="align-middle">
                            {formatToTwoDecimal(item.quantity)}
                          </td>
                          <td className="align-middle">{item.moisture}%</td>
                          <td className="align-middle">
                            {formatToTwoDecimal(item.netWeight)}
                          </td>
                          <td className="align-middle">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="0.00"
                              readOnly
                              value={
                                (item.quantityToReturn = formatToTwoDecimal(
                                  item.netWeight,
                                ))
                              }
                              onChange={(e) => {
                                const value = e.target.value;
                                let inputValue = String(value).replace(
                                  /[^0-9.]/g,
                                  "",
                                );
                                let [integerPart, decimalPart] =
                                  inputValue.split(".");
                                if (integerPart) {
                                  integerPart = integerPart.replace(
                                    /\B(?=(\d{3})+(?!\d))/g,
                                    ",",
                                  );
                                }
                                let formattedValue =
                                  decimalPart !== undefined
                                    ? `${integerPart}.${decimalPart}`
                                    : integerPart;
                                const cleanedValue = formattedValue.replace(
                                  /^0+,|^0+/,
                                  "",
                                );
                                setItemsToReturn((prev) =>
                                  prev.map((returnItem, i) =>
                                    i !== index
                                      ? returnItem
                                      : {
                                          ...returnItem,
                                          quantityToReturn: cleanedValue,
                                        },
                                  ),
                                );
                              }}
                            />
                          </td>
                          <td className="align-middle">
                            {formatToTwoDecimal(
                              Math.max(
                                0,
                                parseNumber(item.static_netWeight) -
                                  parseNumber(item.netWeight),
                              ),
                            )}
                          </td>
                        </tr>
                      </CSSTransition>
                    ))}
                  </TransitionGroup>
                </tbody>
              </table>
            </div>
            <div className="d-flex justify-content-end mb-2">
              <Button
                variant="primary"
                onClick={handleBulkReturn}
                disabled={selectedItems.size === 0}
              >
                Return Selected Items ({selectedItems.size})
              </Button>
            </div>
            <div className="pb-2">
              <PaginationControls {...backloadPagination} />
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <ExportOptionsModal
        exportFormat={exportFormat}
        onHide={() => setExportFormat(null)}
        config={payableExportConfig}
        filters={{ currencyId }}
        defaultRange={displayCutOff}
        onPdfReady={pdfPreview.open}
      />
      <PdfPreviewModal {...pdfPreview.modalProps} />
    </div>
  );
};

export const RefreshContext = createContext(0);

export default Payable;
